import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import Price from "../../components/common/Price";
import { Button } from "../../components/ui/button";
import { CheckCircle2, Loader2, Sparkles, ShieldCheck, Zap, Award, X, Bell, BarChart3 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Link } from "react-router-dom";

const inr = (v) => "₹" + Number(v).toLocaleString("en-IN");

export default function FounderPremium() {
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [waitlist, setWaitlist] = useState(null);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  const load = () => {
    api
      .get("/startup/founder-premium/status")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get("/startup/founder-premium/waitlist/status")
      .then((r) => setWaitlist(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const checkout = async () => {
    setConfirming(true);
    try {
      const { data: r } = await api.post("/startup/founder-premium/checkout");
      toast.success("Founder Premium activated (mocked payment)");
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel Founder Premium? Your featured listing + priority review will end at quarter close.")) return;
    setCancelling(true);
    try {
      await api.post("/startup/founder-premium/cancel");
      toast.success("Premium cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const joinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await api.post("/startup/founder-premium/waitlist");
      toast.success("You're on the waitlist — we'll email you the moment a slot opens.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setJoiningWaitlist(false);
    }
  };

  if (!data) {
    return (
      <DashboardShell title="Founder Premium" subtitle="STARTUP · UPGRADE">
        <div className="text-ink-muted">
          <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
        </div>
      </DashboardShell>
    );
  }

  const cap = data.capacity;
  const tier = data.tier;
  const capPct = Math.min(100, cap.pct_used);
  const nearFull = capPct >= 80;

  return (
    <DashboardShell title="Founder Premium" subtitle="STARTUP · UPGRADE">
      <Toaster richColors position="top-right" />

      {data.active ? (
        <Card testId="fp-active-card" className="mb-6 border-emerald-500/50 bg-gradient-to-br from-emerald-50 to-gold/[0.04]">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-emerald-500/20">
              <Award size={20} className="text-emerald-700" />
            </div>
            <div className="flex-1">
              <div className="overline text-emerald-700">Active Subscription</div>
              <div className="font-heading text-2xl text-ink mt-0.5">
                Founder Premium — you&apos;re in.
              </div>
              <div className="text-xs text-ink-muted mt-1 font-mono">
                Activated {data.subscription?.activated_at?.slice(0, 10)} · Mocked payment ref: {data.subscription?.mocked_payment_ref}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/startup/premium-analytics"
                  className="inline-flex items-center gap-2 bg-navy text-gold px-4 py-2 rounded-md text-sm font-heading hover:bg-navy/90 transition"
                  data-testid="fp-analytics-link"
                >
                  <BarChart3 size={14} /> View ROI Analytics
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancel}
                  disabled={cancelling}
                  data-testid="fp-cancel-btn"
                >
                  {cancelling ? <Loader2 className="animate-spin" size={14} /> : "Cancel subscription"}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* Hero */}
          <Card testId="fp-hero-card" className="mb-6 border-gold bg-gradient-to-br from-navy/[0.02] to-gold/[0.06]">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-1">
                <div className="overline text-gold">VENTURAZ · FOUNDER PREMIUM</div>
                <h2 className="font-heading text-3xl md:text-4xl text-ink mt-2">
                  {tier.tagline}
                </h2>
                <p className="text-sm text-ink mt-3 leading-relaxed">
                  Everything a serious founder needs to get in front of institutional capital — faster, verified, and with a partner on your side.
                </p>
                <div className="mt-5">
                  <Price
                    price={tier.price_inr}
                    originalPrice={tier.original_price_inr}
                    size="xl"
                    suffix={`/ ${tier.period}`}
                    testId="fp-hero-price"
                  />
                </div>
              </div>

              {/* Capacity indicator */}
              <div
                className="w-full md:w-80 border border-navy/[0.1] rounded-md p-4 bg-white"
                data-testid="fp-capacity-card"
              >
                <div className="overline mb-2 flex items-center gap-2">
                  <Zap size={12} className={nearFull ? "text-rose-600" : "text-emerald-600"} />
                  Live Capacity — this quarter
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-heading text-3xl text-ink">{cap.remaining}</span>
                  <span className="text-sm text-ink-muted">of {cap.capacity} slots left</span>
                </div>
                <div className="h-2 bg-navy/[0.06] rounded-full overflow-hidden">
                  <div
                    className={
                      "h-full rounded-full transition-all " +
                      (nearFull ? "bg-rose-500" : capPct >= 50 ? "bg-gold" : "bg-emerald-500")
                    }
                    style={{ width: `${Math.max(6, capPct)}%` }}
                    data-testid="fp-capacity-bar"
                  />
                </div>
                <div className="mt-2 text-[11px] font-mono uppercase text-ink-muted tracking-wider">
                  {cap.taken} founders on-boarded · resets Oct 1
                </div>
                {nearFull && (
                  <div className="mt-2">
                    <Pill tone="danger">Only {cap.remaining} slots — decide fast</Pill>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6">
              {cap.remaining === 0 && waitlist?.on_waitlist ? (
                <div
                  className="border border-gold/50 rounded-md p-4 bg-gold/10"
                  data-testid="fp-waitlist-active"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Bell size={14} className="text-gold" />
                    <span className="overline text-gold">On Waitlist · Position #{waitlist.position}</span>
                  </div>
                  <p className="text-sm text-ink">
                    We&apos;ll email you the moment a slot opens — either when a founder cancels or when {waitlist.total_waiting > 1 ? "the" : "a"} new quarter starts. {waitlist.total_waiting} founder{waitlist.total_waiting !== 1 ? "s are" : " is"} ahead of the queue.
                  </p>
                </div>
              ) : cap.remaining === 0 ? (
                <Button
                  size="lg"
                  onClick={joinWaitlist}
                  disabled={joiningWaitlist}
                  data-testid="fp-join-waitlist-btn"
                  variant="outline"
                  className="border-gold text-navy"
                >
                  {joiningWaitlist ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      <Bell size={16} className="mr-2" />
                      Join Waitlist — Get Notified Instantly
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={() => setShowModal(true)}
                  data-testid="fp-upgrade-btn"
                >
                  <Sparkles size={16} className="mr-2" />
                  Upgrade to Founder Premium
                </Button>
              )}
            </div>
          </Card>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Card testId="fp-benefits-card">
              <div className="overline mb-3">What&apos;s included</div>
              <ul className="space-y-2">
                {tier.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink">
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card testId="fp-guarantees-card">
              <div className="overline mb-3">Our promise</div>
              <ul className="space-y-2">
                {tier.guarantees.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink">
                    <ShieldCheck size={14} className="text-navy mt-0.5 flex-shrink-0" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </>
      )}

      {/* Checkout modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 backdrop-blur-sm p-4" data-testid="fp-checkout-modal">
          <div className="bg-white rounded-md shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-navy/[0.05]"
              data-testid="fp-modal-close"
            >
              <X size={16} />
            </button>
            <div className="overline text-gold mb-2">Confirm Upgrade</div>
            <h3 className="font-heading text-2xl text-ink">Founder Premium</h3>
            <p className="text-sm text-ink-muted mt-2">
              You&apos;re about to activate a mocked-payment Founder Premium subscription for one quarter.
            </p>
            <div className="border border-navy/[0.08] rounded-md p-3 my-4 bg-navy/[0.02]">
              <div className="flex justify-between items-center text-sm">
                <span className="text-ink-muted">Amount</span>
                <Price
                  price={tier.price_inr}
                  originalPrice={tier.original_price_inr}
                  size="sm"
                  align="right"
                  testId="fp-modal-price"
                />
              </div>
              <div className="flex justify-between text-xs text-ink-muted mt-2">
                <span>Payment method</span>
                <span>Mocked (VenturaZ Sandbox)</span>
              </div>
              <div className="flex justify-between text-xs text-ink-muted mt-1">
                <span>Slots remaining before you</span>
                <span className="font-mono">{cap.remaining} / {cap.capacity}</span>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowModal(false)} data-testid="fp-modal-cancel">
                Not now
              </Button>
              <Button onClick={checkout} disabled={confirming} data-testid="fp-modal-confirm">
                {confirming ? <Loader2 className="animate-spin" size={14} /> : "Confirm & Activate"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
